import type { AppLanguage } from "../app-settings/settings.ts";

export type LoginFormInput = {
  email: string;
  password: string;
};

export type LoginPayload =
  | {
      ok: true;
      data: LoginFormInput;
    }
  | {
      ok: false;
      error: string;
    };

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const errorMessages = {
  en: {
    invalidEmail: "Please enter a valid email address.",
    missingPassword: "Please enter your password.",
    invalidCredentials: "Account not found or incorrect password",
    network: "Unable to connect. Check your connection and try again.",
    service: "Sign-in is temporarily unavailable. Please try again later.",
  },
  zh: {
    invalidEmail: "请输入有效的邮箱地址。",
    missingPassword: "请输入密码。",
    invalidCredentials: "账号不存在或密码错误",
    network: "无法连接，请检查网络后重试。",
    service: "登录服务暂时不可用，请稍后重试。",
  },
} as const;

export type LoginErrorCode = keyof typeof errorMessages.en;

export function getLoginErrorMessage(code: LoginErrorCode, language: AppLanguage = "en"): string {
  return errorMessages[language][code];
}

export function getLoginFailureCode(status: number | undefined): LoginErrorCode {
  if (status === 401) return "invalidCredentials";
  return status === undefined ? "network" : "service";
}

export function validateLoginEmail(value: string, language: AppLanguage = "en"):
  | { ok: true; email: string }
  | { ok: false; error: string } {
  const email = value.trim().toLowerCase();

  if (!emailPattern.test(email)) {
    return {
      ok: false,
      error: getLoginErrorMessage("invalidEmail", language),
    };
  }

  return { ok: true, email };
}

export function buildLoginPayload(input: LoginFormInput, language: AppLanguage = "en"): LoginPayload {
  const email = validateLoginEmail(input.email, language);
  const password = input.password.trim();

  if (!email.ok) return email;

  if (!password) {
    return {
      ok: false,
      error: getLoginErrorMessage("missingPassword", language),
    };
  }

  return {
    ok: true,
    data: {
      email: email.email,
      password,
    },
  };
}
